/**
 * 井字棋游戏逻辑
 * 支持3x3、5x5、7x7三种棋盘大小
 * 3x3：三连获胜，5x5和7x7：四连获胜
 */

import {
  Player,
  Position,
  TicTacToeGameState,
  TicTacToeMove,
  TicTacToeBoardSize,
  GameMode,
  Difficulty,
  TICTACTOE_CONFIGS
} from '../types/game';

// ==================== 基础工具函数 ====================

/**
 * 检查位置是否在棋盘内
 */
export const isValidPosition = (position: Position, boardSize: number): boolean => {
  return position.row >= 0 && position.row < boardSize &&
         position.col >= 0 && position.col < boardSize;
};

/**
 * 检查是否还有空位
 */
export const hasEmptyCells = (board: (Player | null)[][]): boolean => {
  return board.some(row => row.some(cell => cell === null));
};

/**
 * 检查某个方向是否形成获胜连线
 */
export const checkDirection = (
  board: (Player | null)[][],
  position: Position,
  player: Player,
  deltaRow: number,
  deltaCol: number,
  winLength: number
): Position[] | null => {
  const boardSize = board.length;
  const line: Position[] = [position];

  // 向正方向检查
  let r = position.row + deltaRow;
  let c = position.col + deltaCol;
  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && board[r][c] === player) {
    line.push({ row: r, col: c });
    r += deltaRow;
    c += deltaCol;
  }

  // 向负方向检查
  r = position.row - deltaRow;
  c = position.col - deltaCol;
  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && board[r][c] === player) {
    line.unshift({ row: r, col: c });
    r -= deltaRow;
    c -= deltaCol;
  }

  // 检查连线长度是否达到获胜条件
  if (line.length >= winLength) {
    return line.slice(0, winLength); // 返回获胜连线
  }

  return null;
};

/**
 * 检查是否获胜
 */
export const checkWinner = (
  board: (Player | null)[][],
  lastMove: Position,
  player: Player,
  winLength: number
): Position[] | null => {
  // 检查四个方向：水平、垂直、两条对角线
  const directions = [
    { deltaRow: 0, deltaCol: 1 },   // 水平
    { deltaRow: 1, deltaCol: 0 },   // 垂直
    { deltaRow: 1, deltaCol: 1 },   // 主对角线
    { deltaRow: 1, deltaCol: -1 }   // 副对角线
  ];

  for (const { deltaRow, deltaCol } of directions) {
    const line = checkDirection(board, lastMove, player, deltaRow, deltaCol, winLength);
    if (line) {
      return line;
    }
  }

  return null;
};

// ==================== 棋盘和棋子设置 ====================

/**
 * 创建初始井字棋棋盘
 */
export const createTicTacToeBoard = (boardSize: TicTacToeBoardSize): (Player | null)[][] => {
  return Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
};

/**
 * 创建初始游戏状态
 */
export const createInitialGameState = (
  boardSize: TicTacToeBoardSize = 3,
  gameMode: GameMode = 'pvp',
  difficulty?: Difficulty
): TicTacToeGameState => {
  const board = createTicTacToeBoard(boardSize);
  const winLength = boardSize === 3 ? 3 : 4; // 3x3三连获胜，其他四连获胜

  return {
    board,
    currentPlayer: 'black', // 黑方先行
    status: 'playing',
    winner: null,
    moves: [],
    gameHistory: [],
    gameMode,
    gameType: 'tictactoe',
    difficulty,
    isAIThinking: false,

    // 井字棋特有状态
    boardSize,
    winLength,
    totalMoves: 0,
    lastMove: undefined,
    winningLine: undefined,
  };
};

// ==================== 游戏规则验证 ====================

/**
 * 检查移动是否合法
 */
export const isValidMove = (
  board: (Player | null)[][],
  position: Position,
  boardSize: number
): boolean => {
  // 检查位置是否在棋盘内
  if (!isValidPosition(position, boardSize)) {
    return false;
  }

  // 检查位置是否为空
  return board[position.row][position.col] === null;
};

/**
 * 获取所有可能的移动
 */
export const getAllPossibleMoves = (board: (Player | null)[][]): Position[] => {
  const moves: Position[] = [];
  const boardSize = board.length;

  for (let row = 0; row < boardSize; row++) {
    for (let col = 0; col < boardSize; col++) {
      if (board[row][col] === null) {
        moves.push({ row, col });
      }
    }
  }

  return moves;
};

/**
 * 检查游戏是否结束
 */
export const checkGameEnd = (
  gameState: TicTacToeGameState
): { isEnd: boolean; winner: Player | null; reason: string } => {
  // 如果有最后一步移动，检查是否获胜
  if (gameState.lastMove) {
    const winningLine = checkWinner(
      gameState.board,
      gameState.lastMove,
      gameState.currentPlayer === 'black' ? 'white' : 'black', // 上一步的玩家
      gameState.winLength
    );

    if (winningLine) {
      const winner = gameState.currentPlayer === 'black' ? 'white' : 'black';
      return {
        isEnd: true,
        winner,
        reason: `${winner === 'black' ? '黑方' : '白方'}获胜！`
      };
    }
  }

  // 检查是否还有空位（平局）
  if (!hasEmptyCells(gameState.board)) {
    return { isEnd: true, winner: null, reason: '平局！' };
  }

  return { isEnd: false, winner: null, reason: '' };
};

// ==================== 游戏操作 ====================

/**
 * 执行移动
 */
export const makeMove = (
  gameState: TicTacToeGameState,
  position: Position
): TicTacToeGameState => {
  // 验证移动是否合法
  if (!isValidMove(gameState.board, position, gameState.boardSize)) {
    return gameState;
  }

  // 创建新棋盘
  const newBoard = gameState.board.map(row => [...row]);

  // 放置棋子
  newBoard[position.row][position.col] = gameState.currentPlayer;

  // 创建移动记录
  const move: TicTacToeMove = {
    position,
    player: gameState.currentPlayer,
    timestamp: Date.now()
  };

  // 检查是否获胜
  const winningLine = checkWinner(newBoard, position, gameState.currentPlayer, gameState.winLength);

  // 检查游戏是否结束
  const gameEndResult = checkGameEnd({
    ...gameState,
    board: newBoard,
    moves: [...gameState.moves, move],
    totalMoves: gameState.totalMoves + 1,
    lastMove: position,
    currentPlayer: gameState.currentPlayer === 'black' ? 'white' : 'black',
    winningLine
  });

  // 创建新游戏状态
  const newGameState: TicTacToeGameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: gameState.currentPlayer === 'black' ? 'white' : 'black',
    moves: [...gameState.moves, move],
    gameHistory: [...gameState.gameHistory, gameState],
    totalMoves: gameState.totalMoves + 1,
    lastMove: position,
    winningLine,

    // 如果游戏结束，更新状态
    status: gameEndResult.isEnd ? 'finished' : 'playing',
    winner: gameEndResult.winner,
  };

  return newGameState;
};

/**
 * 悔棋功能
 */
export const undoMove = (gameState: TicTacToeGameState): TicTacToeGameState => {
  if (gameState.gameHistory.length === 0) {
    return gameState; // 没有历史记录，无法悔棋
  }

  // 获取上一个游戏状态
  const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];

  return {
    ...previousState,
    winningLine: undefined, // 清除获胜连线
  };
};

/**
 * 重新开始游戏
 */
export const restartGame = (
  boardSize: TicTacToeBoardSize,
  gameMode: GameMode,
  difficulty?: Difficulty
): TicTacToeGameState => {
  return createInitialGameState(boardSize, gameMode, difficulty);
};

// ==================== 游戏状态管理 ====================

/**
 * 获取当前游戏状态描述
 */
export const getGameStatusText = (gameState: TicTacToeGameState): string => {
  if (gameState.status === 'finished') {
    if (gameState.winner) {
      return `${gameState.winner === 'black' ? '黑方' : '白方'}获胜！`;
    } else {
      return '平局！';
    }
  }

  return `${gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合`;
};

/**
 * 获取棋盘大小描述
 */
export const getBoardSizeText = (boardSize: TicTacToeBoardSize): string => {
  switch (boardSize) {
    case 3:
      return '3×3（三连获胜）';
    case 5:
      return '5×5（四连获胜）';
    case 7:
      return '7×7（四连获胜）';
    default:
      return `${boardSize}×${boardSize}`;
  }
};

export default {
  // 基础函数
  isValidPosition,
  hasEmptyCells,
  checkDirection,
  checkWinner,

  // 棋盘和棋子
  createTicTacToeBoard,
  createInitialGameState,

  // 游戏规则
  isValidMove,
  getAllPossibleMoves,
  checkGameEnd,

  // 游戏操作
  makeMove,
  undoMove,
  restartGame,

  // 工具函数
  getGameStatusText,
  getBoardSizeText
};